import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Button, ButtonText, HStack, Icon, Spinner, Text, VStack,
} from '@gluestack-ui/themed';
import { ChevronDown, ChevronUp } from 'lucide-react-native';
import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';

import { OralExamMediaSource } from '@/common/api/models/oralExamMediaSource';
import { OralExamPauseReason } from '@/common/api/models/oralExamPauseReason';
import {
  clientRestorableOpenSources,
  isRecordingIntegrityOpen,
} from '@/common/oralExam/mediaIntegritySources';

function siteHostForPermissionHelp(): string {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  return 'this site';
}

type PauseCopy = {
  titleKey: string;
  titleDefault: string;
  bodyKey: string;
  bodyDefault: string;
};

const PAUSE_COPY: Record<OralExamPauseReason, PauseCopy> = {
  lecturer_pause: {
    titleKey: 'oralExam.pause.lecturerPause.title',
    titleDefault: 'Paused by your supervisor',
    bodyKey: 'oralExam.pause.lecturerPause.body',
    bodyDefault: 'The clock is stopped. Please hold on.',
  },
  help_requested: {
    titleKey: 'oralExam.pause.helpRequested.title',
    titleDefault: 'Help requested — clock paused',
    bodyKey: 'oralExam.pause.helpRequested.body',
    bodyDefault: 'Your supervisor has been notified and will join you.',
  },
  media_disabled: {
    titleKey: 'oralExam.pause.mediaDisabled.title',
    titleDefault: 'Exam paused — restore your devices',
    bodyKey: 'oralExam.pause.mediaDisabled.body',
    bodyDefault: "We've paused to protect your exam. Restore the devices below to resume.",
  },
  technical: {
    titleKey: 'oralExam.pause.technical.title',
    titleDefault: 'Exam paused',
    bodyKey: 'oralExam.pause.technical.body',
    bodyDefault: 'The clock is paused on the server while we resolve a connection issue.',
  },
};

const CONFIRMING_RESTORE_COPY: PauseCopy = {
  titleKey: 'oralExam.pause.mediaDisabled.confirmingTitle',
  titleDefault: 'Confirming restore…',
  bodyKey: 'oralExam.pause.mediaDisabled.confirmingBody',
  bodyDefault: 'Your devices look good locally. We are confirming with the server so the exam clock can resume.',
};

const RECONNECTED_COPY: PauseCopy = {
  titleKey: 'oralExam.pause.technical.reconnectedTitle',
  titleDefault: "You're back",
  bodyKey: 'oralExam.pause.technical.reconnectedBody',
  bodyDefault: 'Your connection is back. Resuming your exam…',
};

const RECORDING_RESTART_COPY: PauseCopy = {
  titleKey: 'oralExam.pause.recordingRestart.title',
  titleDefault: 'Exam paused',
  bodyKey: 'oralExam.pause.recordingRestart.body',
  bodyDefault: 'Restarting the recording — your clock is paused.',
};

const STILL_CONFIRMING_AFTER_MS = 10_000;

type SelfHealButton = {
  source: OralExamMediaSource;
  labelKey: string;
  labelDefault: string;
  testID: string;
};

const SELF_HEAL_BUTTONS: Partial<Record<OralExamMediaSource, SelfHealButton>> = {
  [OralExamMediaSource.camera]: {
    source: OralExamMediaSource.camera,
    labelKey: 'oralExam.pause.selfHeal.enableCamera',
    labelDefault: 'Turn camera on',
    testID: 'oral-exam-pause-enable-camera',
  },
  [OralExamMediaSource.microphone]: {
    source: OralExamMediaSource.microphone,
    labelKey: 'oralExam.pause.selfHeal.unmuteMic',
    labelDefault: 'Unmute microphone',
    testID: 'oral-exam-pause-unmute-mic',
  },
  [OralExamMediaSource.screen]: {
    source: OralExamMediaSource.screen,
    labelKey: 'oralExam.pause.selfHeal.reshareScreen',
    labelDefault: 'Re-share screen',
    testID: 'oral-exam-pause-reshare-screen',
  },
};

export type PauseOverlayProps = {
  pauseReason: OralExamPauseReason;
  /** Sources the client currently sees as down; drives per-source self-heal buttons. */
  openMediaSources?: OralExamMediaSource[];
  /** Server-projected consecutive technical-pause budget (Slice S3). */
  remainingConsecutiveSeconds?: number;
  /** LiveKit is Connected while the server still reports a connection outage. */
  connectionRestoredLocally?: boolean;
  /** Browser refused the last self-heal attempt for this source (Slice S4). */
  permissionDeniedSource?: OralExamMediaSource | null;
  onReshareScreen?: () => void;
  onEnableCamera?: () => void;
  onUnmuteMic?: () => void;
  /** `blocking` covers the question body; `banner` is the inline strip above content. */
  variant?: 'banner' | 'blocking';
};

/** Unified pause banner keyed by OralExamPauseReason, with per-source self-heal (§3.5). */
export function PauseOverlay({
  pauseReason,
  openMediaSources = [],
  remainingConsecutiveSeconds,
  connectionRestoredLocally = false,
  permissionDeniedSource = null,
  onReshareScreen,
  onEnableCamera,
  onUnmuteMic,
  variant = 'banner',
}: PauseOverlayProps) {
  const { t } = useTranslation();
  const siteHost = useMemo(() => siteHostForPermissionHelp(), []);
  const [permissionHelpExpanded, setPermissionHelpExpanded] = useState(false);
  const [stillConfirmingVisible, setStillConfirmingVisible] = useState(false);
  const stillConfirmingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectionRestoredSessionRef = useRef(false);
  const sessionSawConnectionOutageRef = useRef(false);
  const hasConnectionOpen = openMediaSources.includes(OralExamMediaSource.connection);
  const hasRecordingOpen = isRecordingIntegrityOpen(openMediaSources);
  const mediaDeviceSources = clientRestorableOpenSources(openMediaSources);
  const isConnectionOnly = hasConnectionOpen && !hasRecordingOpen && mediaDeviceSources.length === 0;
  const isRecordingOnlyPause = hasRecordingOpen && !hasConnectionOpen && mediaDeviceSources.length === 0;

  useEffect(() => {
    if (hasConnectionOpen) {
      sessionSawConnectionOutageRef.current = true;
    }
  }, [hasConnectionOpen]);

  useEffect(() => {
    if (pauseReason !== OralExamPauseReason.technical
      && pauseReason !== OralExamPauseReason.media_disabled
      && openMediaSources.length === 0) {
      sessionSawConnectionOutageRef.current = false;
    }
  }, [openMediaSources.length, pauseReason]);

  const showReconnectedCopy = mediaDeviceSources.length === 0 && (
    connectionRestoredLocally
    || (isRecordingOnlyPause && sessionSawConnectionOutageRef.current)
  );
  const isMediaDisabled = pauseReason === OralExamPauseReason.media_disabled
    || mediaDeviceSources.length > 0;
  const isTechnicalPause = pauseReason === OralExamPauseReason.technical
    || isConnectionOnly
    || (hasConnectionOpen && mediaDeviceSources.length === 0);
  const confirmingRestore = isMediaDisabled && !isTechnicalPause && mediaDeviceSources.length === 0;
  const copy = showReconnectedCopy
    ? RECONNECTED_COPY
    : (isRecordingOnlyPause
      ? RECORDING_RESTART_COPY
      : (confirmingRestore
        ? CONFIRMING_RESTORE_COPY
        : (isTechnicalPause
          ? PAUSE_COPY.technical
          : (isMediaDisabled ? PAUSE_COPY.media_disabled : PAUSE_COPY[pauseReason]))));

  useEffect(() => {
    if (!showReconnectedCopy) {
      connectionRestoredSessionRef.current = false;
      setStillConfirmingVisible(false);
      if (stillConfirmingTimerRef.current != null) {
        clearTimeout(stillConfirmingTimerRef.current);
        stillConfirmingTimerRef.current = null;
      }
      return undefined;
    }

    if (!connectionRestoredSessionRef.current) {
      connectionRestoredSessionRef.current = true;
      stillConfirmingTimerRef.current = setTimeout(() => {
        setStillConfirmingVisible(true);
      }, STILL_CONFIRMING_AFTER_MS);
    }

    return () => {
      if (stillConfirmingTimerRef.current != null) {
        clearTimeout(stillConfirmingTimerRef.current);
        stillConfirmingTimerRef.current = null;
      }
    };
  }, [showReconnectedCopy]);

  const handlers: Partial<Record<OralExamMediaSource, (() => void) | undefined>> = {
    [OralExamMediaSource.camera]: onEnableCamera,
    [OralExamMediaSource.microphone]: onUnmuteMic,
    [OralExamMediaSource.screen]: onReshareScreen,
  };

  const blocking = variant === 'blocking';
  const showStillConfirming = showReconnectedCopy
    && stillConfirmingVisible
    && remainingConsecutiveSeconds != null
    && remainingConsecutiveSeconds > 0;
  const showRemainingSeconds = remainingConsecutiveSeconds != null
    && remainingConsecutiveSeconds > 0
    && (isTechnicalPause || isMediaDisabled || isRecordingOnlyPause)
    && !showReconnectedCopy;
  const permissionDeniedDeviceSource = permissionDeniedSource === OralExamMediaSource.camera
    || permissionDeniedSource === OralExamMediaSource.microphone
    || permissionDeniedSource === OralExamMediaSource.screen
    ? permissionDeniedSource
    : null;
  const showPermissionHelp = permissionDeniedDeviceSource != null
    && mediaDeviceSources.includes(permissionDeniedDeviceSource);
  const showHowToAllowToggle = showPermissionHelp
    && (permissionDeniedDeviceSource === OralExamMediaSource.camera
      || permissionDeniedDeviceSource === OralExamMediaSource.microphone);

  const permissionBlockedKey = permissionDeniedDeviceSource === OralExamMediaSource.camera
    ? 'oralExam.pause.mediaDisabled.blockedCamera'
    : permissionDeniedDeviceSource === OralExamMediaSource.microphone
      ? 'oralExam.pause.mediaDisabled.blockedMicrophone'
      : permissionDeniedDeviceSource === OralExamMediaSource.screen
        ? 'oralExam.pause.mediaDisabled.blockedScreen'
        : null;
  const howToAllowKey = permissionDeniedDeviceSource === OralExamMediaSource.camera
    ? 'oralExam.pause.mediaDisabled.howToAllowCamera'
    : permissionDeniedDeviceSource === OralExamMediaSource.microphone
      ? 'oralExam.pause.mediaDisabled.howToAllowMicrophone'
      : null;

  useEffect(() => {
    if (!showPermissionHelp) {
      setPermissionHelpExpanded(false);
    }
  }, [showPermissionHelp]);

  return (
    <Box
      testID="oral-exam-pause-overlay"
      position={blocking ? 'absolute' : undefined}
      top={blocking ? 0 : undefined}
      left={blocking ? 0 : undefined}
      right={blocking ? 0 : undefined}
      bottom={blocking ? 0 : undefined}
      zIndex={blocking ? 20 : undefined}
      justifyContent={blocking ? 'center' : undefined}
      bg={blocking ? '$bgApp' : '$bgBrandSubtle'}
      borderLeftWidth={blocking ? 0 : 4}
      borderLeftColor={blocking ? undefined : '$brandBase'}
      px={blocking ? '$6' : '$4'}
      py={blocking ? '$6' : '$3'}
      borderRadius={blocking ? undefined : '$md'}
    >
      <VStack
        space={blocking ? 'md' : 'sm'}
        testID="oral-exam-student-paused"
        maxWidth={blocking ? 480 : undefined}
        alignSelf={blocking ? 'center' : undefined}
        width={blocking ? '100%' : undefined}
      >
        <VStack
          space="xs"
          alignItems={blocking ? 'center' : undefined}
          testID={showReconnectedCopy ? 'oral-exam-pause-reconnected' : undefined}
        >
          <Text
            size={blocking ? 'xl' : 'md'}
            fontWeight="semibold"
            color="$textHeading"
            textAlign={blocking ? 'center' : undefined}
          >
            {t(copy.titleKey, { defaultValue: copy.titleDefault })}
          </Text>
          {showReconnectedCopy ? (
            <HStack
              space="sm"
              alignItems="center"
              justifyContent={blocking ? 'center' : 'flex-start'}
            >
              <Spinner size="small" />
              <Text
                size={blocking ? 'md' : 'sm'}
                color="$textBody"
                textAlign={blocking ? 'center' : undefined}
                flexShrink={1}
              >
                {t(copy.bodyKey, { defaultValue: copy.bodyDefault })}
              </Text>
            </HStack>
          ) : (
            <Text
              size={blocking ? 'md' : 'sm'}
              color="$textBody"
              textAlign={blocking ? 'center' : undefined}
            >
              {t(copy.bodyKey, { defaultValue: copy.bodyDefault })}
            </Text>
          )}
          {showStillConfirming ? (
            <Text
              size="sm"
              color="$textSubtle"
              textAlign={blocking ? 'center' : undefined}
              testID="oral-exam-pause-still-confirming"
            >
              {t('oralExam.pause.technical.stillConfirming', {
                count: remainingConsecutiveSeconds,
                defaultValue: 'Still confirming — {{count}} seconds left',
              })}
            </Text>
          ) : null}
          {showRemainingSeconds ? (
            <Text
              size="sm"
              color="$textSubtle"
              textAlign={blocking ? 'center' : undefined}
              testID="oral-exam-pause-remaining-seconds"
            >
              {t('oralExam.pause.technical.remainingSeconds', {
                count: remainingConsecutiveSeconds,
                defaultValue: '{{count}} seconds remaining before this session is invalidated under exam regulations',
              })}
            </Text>
          ) : null}
        </VStack>
        {isMediaDisabled && mediaDeviceSources.length > 0 ? (
          <VStack space="xs" alignItems={blocking ? 'center' : undefined} width={blocking ? '100%' : undefined}>
            {mediaDeviceSources.map((source) => {
              const button = SELF_HEAL_BUTTONS[source];
              const onPress = handlers[source];
              if (!button || !onPress) return null;
              return (
                <Button
                  key={source}
                  size={blocking ? 'lg' : 'sm'}
                  action="primary"
                  alignSelf={blocking ? 'center' : 'flex-start'}
                  minWidth={blocking ? 220 : undefined}
                  onPress={onPress}
                  testID={button.testID}
                >
                  <ButtonText>
                    {t(button.labelKey, { defaultValue: button.labelDefault })}
                  </ButtonText>
                </Button>
              );
            })}
          </VStack>
        ) : null}
        {showPermissionHelp && permissionBlockedKey ? (
          <VStack space="xs" width="100%" alignItems={blocking ? 'center' : undefined}>
            <Text
              size="sm"
              color="$textBody"
              textAlign={blocking ? 'center' : 'left'}
              testID="oral-exam-pause-permission-blocked"
            >
              {t(permissionBlockedKey, { host: siteHost })}
            </Text>
            {showHowToAllowToggle && howToAllowKey ? (
              <>
                <Pressable
                  onPress={() => setPermissionHelpExpanded((expanded) => !expanded)}
                  testID="oral-exam-pause-permission-help-toggle"
                  accessibilityRole="button"
                  accessibilityState={{ expanded: permissionHelpExpanded }}
                >
                  <Box minHeight={44} justifyContent="center">
                    <HStack space="xs" alignItems="center" justifyContent={blocking ? 'center' : 'flex-start'}>
                      <Icon
                        as={permissionHelpExpanded ? ChevronUp : ChevronDown}
                        size="xs"
                        color="$textSubtle"
                      />
                      <Text size="sm" color="$textBrand">
                        {t(howToAllowKey)}
                      </Text>
                    </HStack>
                  </Box>
                </Pressable>
                {permissionHelpExpanded ? (
                  <Box
                    bg="$bgSubtle"
                    borderRadius="$md"
                    p="$4"
                    width="100%"
                  >
                    <VStack space="xs">
                      <Text
                        size="sm"
                        color="$textSubtle"
                        textAlign="left"
                        testID="oral-exam-pause-permission-help-lead"
                      >
                        {t('oralExam.mediaBlockedHelpLead', { host: siteHost })}
                      </Text>
                      <Text
                        size="sm"
                        color="$textSubtle"
                        textAlign="left"
                        testID="oral-exam-pause-permission-help-chrome"
                      >
                        {t('oralExam.mediaBlockedHelpChrome', { host: siteHost })}
                      </Text>
                      <Text
                        size="sm"
                        color="$textSubtle"
                        textAlign="left"
                        testID="oral-exam-pause-permission-help-safari"
                      >
                        {t('oralExam.mediaBlockedHelpSafari', { host: siteHost })}
                      </Text>
                    </VStack>
                  </Box>
                ) : null}
              </>
            ) : null}
          </VStack>
        ) : null}
      </VStack>
    </Box>
  );
}
